/**
 * DeepLinkHandler - Always rendered, regardless of auth state
 * Handles browser-based deep links (magic links, conversation navigation)
 *
 * This component is rendered at the app root to ensure deep links
 * are handled even when the user is logged out.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { useToast } from '@/app/providers/ToastContext';
import { useSession } from '@/features/auth/hooks/useSession';
import { isNative } from '@/lib/capacitor';

const PENDING_DEEPLINK_KEY = 'xavior_pending_deeplink';
const PROCESSED_DEEPLINK_KEY = 'xavior_processed_deeplink';

export function storePendingDeepLink(path: string): void {
  sessionStorage.setItem(PENDING_DEEPLINK_KEY, path);
}

export function consumePendingDeepLink(): string | null {
  const pending = sessionStorage.getItem(PENDING_DEEPLINK_KEY);
  if (pending) {
    sessionStorage.removeItem(PENDING_DEEPLINK_KEY);
  }
  return pending;
}

export function hasPendingDeepLink(): boolean {
  return sessionStorage.getItem(PENDING_DEEPLINK_KEY) !== null;
}

/**
 * Check if a URL has already been processed (prevents re-triggering loops)
 */
function isUrlProcessed(url: string): boolean {
  const processed = sessionStorage.getItem(PROCESSED_DEEPLINK_KEY);
  return processed === url;
}

function markUrlProcessed(url: string): void {
  sessionStorage.setItem(PROCESSED_DEEPLINK_KEY, url);
}

/**
 * Clear the processed URL marker (call on logout or when appropriate)
 */
export function clearProcessedDeepLink(): void {
  sessionStorage.removeItem(PROCESSED_DEEPLINK_KEY);
}

export function DeepLinkHandler() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { status } = useSession();

  // Use refs to avoid re-registering listeners when these change
  const statusRef = useRef(status);
  const navigateRef = useRef(navigate);
  const showToastRef = useRef(showToast);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  // Stable handler using refs - won't cause effect re-runs
  const handleDeepLink = useCallback((url: string) => {
    console.log('[DeepLinkHandler] Handling URL:', url);

    // Check if we've already processed this exact URL
    if (isUrlProcessed(url)) {
      console.log('[DeepLinkHandler] URL already processed, skipping:', url);
      return;
    }

    try {
      // Parse the URL - handles both xavior:// and https://app.xavior.ai/
      let urlToParse = url;
      if (urlToParse.startsWith('xavior://')) {
        urlToParse = urlToParse.replace('xavior://', 'https://app.xavior.ai/');
      }

      const parsedUrl = new URL(urlToParse);
      const path = parsedUrl.pathname + parsedUrl.search;
      const currentStatus = statusRef.current;

      console.log('[DeepLinkHandler] Parsed path:', path, 'Auth status:', currentStatus);

      // Magic-link verification: navigate directly; the MagicLinkVerify page
      // handles auth itself.
      if (path.startsWith('/auth/verify')) {
        // Mark as processed BEFORE navigating to prevent re-processing
        markUrlProcessed(url);
        showToastRef.current('Magic link detected, verifying...', 'info');
        navigateRef.current(path);
        return;
      }

      // SSO denial/error native return (native-oauth-pass Ch.3 F6): a cancelled or
      // failed SSO sends the user back to the in-app login surface via
      // xavior://login?error=<code> (or ?status=…). Route it straight to /login so
      // LoginV2 renders the message — do NOT fall through to the pending-deep-link
      // stash below (that's for post-auth navigation, and the user is logging in,
      // not deep-linking past the gate). Deliberately NOT marked processed: the
      // SSO error URL is deterministic (no nonce), so the single-slot dedupe would
      // permanently swallow a second identical failure (cancel → retry → cancel).
      // Navigating to /login is idempotent, so re-delivery is harmless.
      if (path.startsWith('/login')) {
        navigateRef.current(path);
        return;
      }

      // Native connect return (native-oauth-pass Ch.4): the connect callback sends
      // xavior://integrations/callback?integration_connected=true (or ?error=…) for
      // a Capacitor grant. Route the result params to the app ROOT (the
      // /integrations/callback web route is an inert legacy no-op) with a FULL
      // RELOAD, not a client-side navigate(): App.tsx's OAuth-return handler is a
      // mount-only effect, so it must re-mount to consume integration_connected /
      // error (post-connect refetch + toast) — a client-side navigate would not
      // re-run it. This mirrors the web connect flow (a full-page redirect back with
      // the params); the native session token lives in localStorage, so it survives
      // the reload. Not marked processed: the params are deterministic and a root
      // reload is idempotent.
      if (path.startsWith('/integrations/callback')) {
        // Mark processed BEFORE the reload. On a COLD-START delivery the OS hands
        // the launch URL to App.getLaunchUrl(), and a window.location.href reload
        // does NOT clear the native launch options — so getLaunchUrl() returns this
        // SAME url after the reload, re-entering here and reloading forever. The
        // single-slot dedupe (sessionStorage, which survives a same-origin reload)
        // breaks that loop, exactly as the /auth/verify branch above does. The
        // params remain deterministic and the root reload idempotent; dedupe only
        // prevents the cold-launch infinite-reload. native-oauth-pass Ch.4 review fix.
        markUrlProcessed(url);
        window.location.href = `/${parsedUrl.search}`;
        return;
      }

      if (currentStatus === 'authenticated') {
        console.log('[DeepLinkHandler] Authenticated, navigating to:', path);
        markUrlProcessed(url);
        navigateRef.current(path);
      } else {
        // Not authenticated — stash for navigation after login.
        console.log('[DeepLinkHandler] Not authenticated, storing pending link:', path);
        storePendingDeepLink(path);
        markUrlProcessed(url);
        showToastRef.current('Please log in to continue', 'info');
      }
    } catch (error) {
      console.error('[DeepLinkHandler] Failed to parse URL:', url, error);
    }
  }, []);

  // Process pending deep link after auth completes
  useEffect(() => {
    if (status !== 'authenticated') return;

    const pending = consumePendingDeepLink();
    if (pending) {
      console.log('[DeepLinkHandler] Processing pending deep link after auth:', pending);
      // Small delay to ensure session is fully established
      setTimeout(() => {
        navigateRef.current(pending);
      }, 100);
    }
  }, [status]);

  // Wire native (Capacitor) deep links. iOS delivers magic-link universal links
  // (app.xavior.ai/auth/verify) and the xavior:// SSO return either as a cold
  // start (getLaunchUrl, app launched by the link) or to an already-running app
  // (appUrlOpen). Without this listener the OS opens the app but nothing
  // navigates to /auth/verify, so the login never completes. Web is a no-op:
  // isNative() is false and the web App shim never fires appUrlOpen.
  useEffect(() => {
    if (!isNative()) return;
    let cleanup: (() => void) | undefined;
    App.getLaunchUrl()
      .then((res) => {
        if (res?.url) handleDeepLink(res.url);
      })
      .catch(() => {});
    App.addListener('appUrlOpen', (event) => {
      if (event?.url) handleDeepLink(event.url);
    })
      .then((handle) => {
        cleanup = () => {
          void handle.remove();
        };
      })
      .catch(() => {});
    return () => {
      cleanup?.();
    };
  }, [handleDeepLink]);

  return null;
}
